import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { authClient } from '~/lib/auth-client';
import { Button } from '~/components/button';

export const Route = createFileRoute('/login')({
  component: Login,
});

type Mode = 'signin' | 'signup';

function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('signin');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true);
    setError(null);
    try {
      if (mode === 'signup') {
        const res = await authClient.signUp.email({
          email,
          password,
          name: name || email.split('@')[0] || 'you',
          callbackURL: '/today',
        });
        if (res.error) {
          setError(res.error.message ?? 'could not sign up');
          return;
        }
      } else {
        const res = await authClient.signIn.email({
          email,
          password,
          callbackURL: '/today',
        });
        if (res.error) {
          setError(res.error.message ?? 'could not sign in');
          return;
        }
      }
      navigate({ to: '/today' });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'unexpected error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="mx-auto max-w-md px-4 py-16">
      <h1 className="text-2xl font-semibold tracking-tight">
        {mode === 'signup' ? 'create an account' : 'sign in'}
      </h1>
      <p className="mt-2 text-sm text-muted-foreground">
        email + password.
      </p>

      <form className="mt-8 space-y-3" onSubmit={submit}>
        {mode === 'signup' && (
          <input
            type="text"
            placeholder="your name (optional)"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full h-12 rounded-[var(--radius)] border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
          />
        )}
        <input
          type="email"
          required
          autoComplete="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="w-full h-12 rounded-[var(--radius)] border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
        />
        <input
          type="password"
          required
          minLength={8}
          autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
          placeholder={mode === 'signup' ? 'password (min 8 chars)' : 'password'}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          className="w-full h-12 rounded-[var(--radius)] border border-input bg-background px-3 text-base focus:outline-none focus:ring-2 focus:ring-ring"
        />

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy ? '…' : mode === 'signup' ? 'create account' : 'sign in'}
        </Button>
      </form>

      <p className="mt-6 text-center text-sm text-muted-foreground">
        {mode === 'signup' ? (
          <>
            already have an account?{' '}
            <button
              type="button"
              onClick={() => {
                setMode('signin');
                setError(null);
              }}
              className="underline text-foreground"
            >
              sign in
            </button>
          </>
        ) : (
          <>
            new here?{' '}
            <button
              type="button"
              onClick={() => {
                setMode('signup');
                setError(null);
              }}
              className="underline text-foreground"
            >
              create an account
            </button>
          </>
        )}
      </p>
    </section>
  );
}
